import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ShieldCheck, Zap, HeartHandshake, BatteryCharging } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { APPS_DATA } from '../data/apps';
import { SeoHead } from '../components/common/SeoHead';
import { Button } from '../components/ui/Button';
import { AppCard } from '../components/app/AppCard';

export const HomePage: React.FC = () => {
  const { t, locale } = useI18n();

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kapitalletter',
    url: 'https://project-kapital-app-dev.web.app',
    logo: 'https://project-kapital-app-dev.web.app/favicon.svg',
    description: '일상을 더 편리하게 만드는 모바일 경험을 개발합니다.',
    sameAs: [
      'https://apps.apple.com',
      'https://play.google.com',
    ],
  };

  const philosophies = [
    {
      icon: ShieldCheck,
      title: locale === 'ko' ? '오프라인 우선 & 프라이버시' : 'Offline-First & Privacy',
      desc: locale === 'ko' ? '민감한 개인 데이터를 서버로 수집하지 않고, 오직 기기 내부 안전 영역에만 암호화 보관합니다.' : 'Your data stays encrypted on your device. We do not harvest sensitive data on external servers.',
    },
    {
      icon: Zap,
      title: locale === 'ko' ? '불필요한 군더더기 없는 UI' : 'Clutter-Free Interface',
      desc: locale === 'ko' ? '공격적인 광고, 팝업, 억지 결제 유도를 배제하고 본질적인 기능에 집중할 수 있는 경험을 설계합니다.' : 'No aggressive ads or popups. We build clean, distraction-free experiences focused on core value.',
    },
    {
      icon: HeartHandshake,
      title: locale === 'ko' ? '진정성 있는 피드백 소통' : 'Direct Developer Support',
      desc: locale === 'ko' ? '모든 문의와 버그 제보를 개발팀이 직접 확인하고 24~48시간 이내에 성심껏 피드백을 반영합니다.' : 'Every bug report and feedback is handled directly by engineers with fast turnaround.',
    },
    {
      icon: BatteryCharging,
      title: locale === 'ko' ? '배터리 및 성능 최적화' : 'Resource & Battery Efficiency',
      desc: locale === 'ko' ? '백그라운드 리소스 소모를 최소화하여 오래 켜두어도 기기 발열과 배터리 소모가 적습니다.' : 'Carefully engineered background routines ensure light battery usage and smooth animations.',
    },
  ];

  return (
    <div className="flex flex-col">
      <SeoHead
        title="Kapitalletter | Official Studio & Apps"
        description="일상을 더 편리하게 만드는 모바일 경험을 개발합니다."
        schemaJson={organizationSchema}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 sm:pt-20 pb-16 sm:pb-24 border-b border-slate-100">
        {/* Subtle Ambient Brand Glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-200/20 blur-[120px] rounded-full pointer-events-none -z-10"
          aria-hidden="true"
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Headline */}
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.15] max-w-4xl mx-auto">
            <span className="block mb-4">일상을 더 편리하게 만드는</span>
            <span className="block">모바일 경험을 개발합니다.</span>
          </h1>

          {/* Action CTAs */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto">
            <a href="#showcase" className="w-full sm:w-auto">
              <Button variant="primary" size="lg" fullWidth icon={<ArrowDown className="w-4 h-4" />}>
                {t.home.heroCtaPrimary}
              </Button>
            </a>
            <Link to="/about" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" fullWidth>
                {t.home.heroCtaSecondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Apps Showcase Section */}
      <section id="showcase" className="py-16 sm:py-24 bg-slate-50/60 border-b border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              {t.home.showcaseTitle}
            </h2>
            <p className="mt-3 text-slate-600 text-base sm:text-lg">
              {t.home.showcaseSubtitle}
            </p>
          </div>

          {/* Showcase Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {APPS_DATA.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        </div>
      </section>

      {/* Engineering Philosophy Section */}
      <section className="py-16 sm:py-24 border-b border-slate-200/80 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              {t.home.philosophyTitle}
            </h2>
            <p className="mt-3 text-slate-600 text-sm sm:text-base">
              화려한 마케팅 수식어보다 실제 사용자가 매일 체감하는 품질과 신뢰를 우선시합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {philosophies.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <div
                  key={idx}
                  className="p-6 rounded-2xl border border-slate-200/90 bg-white shadow-xs flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-700 flex items-center justify-center mb-4 border border-primary-200/60">
                      <IconComp className="w-5 h-5" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-base mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Support Direct Banner */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-12 shadow-xs flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 max-w-xl text-center md:text-left">
              <span className="text-xs font-bold text-primary-700 uppercase tracking-wider">
                Support & Help Center
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                {t.home.supportBannerTitle}
              </h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                {t.home.supportBannerDesc}
              </p>
            </div>
            <Link to="/support" className="shrink-0 w-full md:w-auto">
              <Button variant="primary" size="lg" fullWidth className="whitespace-nowrap">
                {t.home.supportBannerCta}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
